/**
 * Report form helpers: the ticket title taken from what people type, the
 * draft kept on this device while they type, and the details (name, email,
 * where they sit) remembered after a report so the next one is quicker.
 */

const DRAFT_KEY = 'helpdesk.reportDraft';
const REPORTER_KEY = 'helpdesk.reporter';
const DRAFT_DAYS = 7;

/**
 * The ticket title: the first sentence of the first line, or the whole line
 * when that sentence is too short to say anything ("Hi."). Cut at a word
 * boundary to fit the 120-character limit.
 */
export function subjectFrom(text: string, max = 120): string {
  const line = text.trim().split('\n')[0]!.trim();
  const sentence = /^.*?[.!?](?=\s|$)/.exec(line)?.[0] ?? line;
  const s = (sentence.length >= 12 ? sentence : line).replace(/[.\s]+$/, '');
  if (s.length <= max) return s;
  const cut = s.slice(0, max - 1);
  const space = cut.lastIndexOf(' ');
  return `${space > max / 2 ? cut.slice(0, space) : cut}…`;
}

export interface ReportDraft {
  category: string;
  text: string;
  personal: boolean;
  serviceMode: string;
  /** When they started typing: the server's spam check measures from here. */
  startedAt: number;
  savedAt: number;
}

export function loadDraft(now = Date.now()): ReportDraft | null {
  try {
    const d = JSON.parse(localStorage.getItem(DRAFT_KEY) ?? 'null') as ReportDraft | null;
    if (!d || typeof d.text !== 'string' || now - d.savedAt > DRAFT_DAYS * 86_400_000) return null;
    return d;
  } catch {
    return null;
  }
}

export function saveDraft(draft: Omit<ReportDraft, 'savedAt'>) {
  try {
    localStorage.setItem(DRAFT_KEY, JSON.stringify({ ...draft, savedAt: Date.now() }));
  } catch {
    // Private browsing or storage full: the draft just isn't kept.
  }
}

export function clearDraft() {
  try {
    localStorage.removeItem(DRAFT_KEY);
  } catch {
    // Nothing to clear.
  }
}

export interface Reporter {
  name?: string;
  email?: string;
  department?: string;
  officeLocation?: string;
  phone?: string;
}

export function loadReporter(): Reporter {
  try {
    return (JSON.parse(localStorage.getItem(REPORTER_KEY) ?? 'null') as Reporter | null) ?? {};
  } catch {
    return {};
  }
}

export function saveReporter(reporter: Reporter) {
  try {
    const kept = Object.fromEntries(Object.entries(reporter).filter(([, v]) => v));
    localStorage.setItem(REPORTER_KEY, JSON.stringify({ ...loadReporter(), ...kept }));
  } catch {
    // Not remembered; they'll type it again next time.
  }
}
